import { Button } from "antd";
import React, { useEffect, useState } from "react";
import Icon, { ICON_NAME } from "../../../components/Icon";
import { callEventChecklistEdit, getChecklistEdit, useChecklistEdit } from "./ChecklistEditContext";
import { ELEMENT_ID, IPropsElement } from "./constants";
import {
    CalendarPopup,
    HeadingSize,
    IsMultiSelections,
    Label,
    MinuteStep,
    NumberToColumns,
    Options,
    Placeholder,
    Require,
    SelectedDefault,
    Separator,
    Signer,
    Size,
    SubLabel,
    TextAlignment,
    TimeField,
    TimeRange,
} from "./RightComponent";
import styles from "./style.module.scss";

interface IProps {
    data: IPropsElement;

    onChange(value: Partial<IPropsElement>): void;
}

const Checkbox: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;

    return (
        <>
            <div className={styles.rightTitle}>Multiple Properties</div>
            <Label label={label} onChange={onChange} />
            <Require properties={properties} onChange={onChange} />
            <Options properties={properties} onChange={onChange} />
            <NumberToColumns properties={properties} onChange={onChange} />
            <SelectedDefault mode="multiple" properties={properties} onChange={onChange} />
        </>
    );
};

const Date: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;

    return (
        <>
            <div className={styles.rightTitle}>Date picker Properties</div>
            <Label label={label} onChange={onChange} />
            <Require properties={properties} onChange={onChange} />
            <Separator properties={properties} onChange={onChange} />
            <CalendarPopup properties={properties} onChange={onChange} />
            <TimeField properties={properties} onChange={onChange} />
            <SubLabel properties={properties} onChange={onChange} />
        </>
    );
};

const Dropdown: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;

    return (
        <>
            <div className={styles.rightTitle}>Dropdown Properties</div>
            <Label label={label} onChange={onChange} />
            <Require properties={properties} onChange={onChange} />
            <Options properties={properties} onChange={onChange} />
            <IsMultiSelections properties={properties} onChange={onChange} />
            <SelectedDefault
                mode={properties.multipleSelections ? "multiple" : undefined}
                properties={properties}
                onChange={onChange}
            />
            <SubLabel properties={properties} onChange={onChange} />
        </>
    );
};

const Header: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;
    return (
        <>
            <div className={styles.rightTitle}>Header Properties</div>
            <Label name="Heading Text" label={label} onChange={onChange} />
            <SubLabel
                name="Sub-Heading Text"
                properties={properties}
                onChange={onChange}
            />
            <HeadingSize properties={properties} onChange={onChange} />
            <TextAlignment properties={properties} onChange={onChange} />
        </>
    );
};

const LongText: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;
    return (
        <>
            <div className={styles.rightTitle}>Long text field Properties</div>
            <Label label={label} onChange={onChange} />
            <Require properties={properties} onChange={onChange} />
            <Size properties={properties} onChange={onChange} />
            <Placeholder properties={properties} onChange={onChange} />
            <SubLabel properties={properties} onChange={onChange} />
        </>
    );
};

const Number: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;
    return (
        <>
            <div className={styles.rightTitle}>Number field Properties</div>
            <Label label={label} onChange={onChange} />
            <Require properties={properties} onChange={onChange} />
            <Placeholder properties={properties} onChange={onChange} />
            <SubLabel properties={properties} onChange={onChange}
            />
        </>
    );
};

const Radio: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;
    return (
        <>
            <div className={styles.rightTitle}>Single choice Properties</div>
            <Label label={label} onChange={onChange} />
            <Require properties={properties} onChange={onChange} />
            <Options properties={properties} onChange={onChange} />
            <NumberToColumns properties={properties} onChange={onChange} />
            <SelectedDefault properties={properties} onChange={onChange} />
        </>
    );
};

const ShortText: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;
    return (
        <>
            <div className={styles.rightTitle}>Short text field Properties</div>
            <Label label={label} onChange={onChange} />
            <Require properties={properties} onChange={onChange} />
            <Placeholder properties={properties} onChange={onChange} />
            <SubLabel properties={properties} onChange={onChange} />
        </>
    );
};

const Sign: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;
    return (
        <>
            <div className={styles.rightTitle}>Sign Properties</div>
            <Label label={label} onChange={onChange} />
            <Signer properties={properties} onChange={onChange} />
        </>
    );
};

const Time: React.FC<IProps> = ({ data, onChange }) => {
    const { label, properties } = data;
    return (
        <>
            <div className={styles.rightTitle}>Time picker Properties</div>
            <Label label={label} onChange={onChange} />
            <Placeholder properties={properties} onChange={onChange} />
            <MinuteStep properties={properties} onChange={onChange} />
            <TimeRange properties={properties} onChange={onChange} />
            <SubLabel properties={properties} onChange={onChange} />
        </>
    );
};

const Divide: React.FC<IProps> = () => {
    return (
        <>
            <div className={styles.rightTitle}>Divide Properties</div>
        </>
    );
};

const Views: { [id: string]: React.FC<IProps> } = {
    [ELEMENT_ID.CHECKBOX]: Checkbox,
    [ELEMENT_ID.DATE]: Date,
    [ELEMENT_ID.DROPDOWN]: Dropdown,
    [ELEMENT_ID.HEADER]: Header,
    [ELEMENT_ID.LONG_TEXT]: LongText,
    [ELEMENT_ID.NUMBER]: Number,
    [ELEMENT_ID.RADIO]: Radio,
    [ELEMENT_ID.SHORT_TEXT]: ShortText,
    [ELEMENT_ID.SIGN]: Sign,
    [ELEMENT_ID.TIME]: Time,
    [ELEMENT_ID.DIVIDE]: Divide,
};

const RightControl: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [selected] = useChecklistEdit((state) => state.selected);
    const [rowElementCount] = useChecklistEdit(state => (
        state.rowSelected === undefined ? 0 : state.form[state.rowSelected]?.length || 0
    ));
    const [data, setData] = useState<{ start: number, finish: number } & IPropsElement | undefined>();
    const get = getChecklistEdit();
    const { onElementUpdate, onElementDelete, onElementDuplicate, onElementChangePosition } = callEventChecklistEdit();

    useEffect(() => {
        if (selected !== undefined) {
            const { form, rowSelected } = get();
            let value;
            if (rowSelected === undefined) {
                setData(undefined);
                return;
            }

            value = form[rowSelected].find((item) => item.index === selected);
            if (value) {
                setOpen(true);
                setData({
                    ...value,
                    start: form[rowSelected][0].subIndex || 0,
                    finish: form[rowSelected][form[rowSelected].length - 1].subIndex || 0
                });
            }
        } else {
            setData(undefined);
            setOpen(false);
        }
    }, [get, selected]);

    const handleChange = (value: Partial<IPropsElement>) => {
        setData(prev => {
            if (!prev)
                return prev;
            const newData = { ...prev, ...value };
            onElementUpdate(newData);
            return newData;
        })
    };
    const { type = "" } = data || {};
    const View = Views[type as string] || (() => <div />);

    const handleDuplication = () => {
        if (data)
            onElementDuplicate(data);
    }

    const changePosition = (value: number) => {
        if (data) {
            onElementChangePosition(data, value);
            setData({ ...data, subIndex: Math.max(0, (data.subIndex || 0) + value) });
        }
    }

    return <div className={styles.right} data-open={open}>
        {data && <div
            className={styles.rightBtOpen}
            onClick={() => setOpen((prev) => !prev)}
        />}
        <div className={styles.rightControl} onFocusCapture={() => setOpen(true)}>
            {data && <>
                <View data={data} onChange={handleChange} />
                {type !== ELEMENT_ID.DIVIDE && rowElementCount > 1 && <div className={styles.rightProperty}>
                    <div className='flex gap-[32px] mb-1'>
                        <Button
                            className='bt-default w-[32px]'
                            data-icon={true}
                            disabled={data.start === (data.subIndex || 0)}
                            onClick={() => changePosition(-1)}
                        >
                            <Icon icon={ICON_NAME.ARROW_LEFT} />
                        </Button>
                        <Button
                            className='bt-default  w-[32px] rotate-180'
                            data-icon={true}
                            disabled={data.finish === (data.subIndex || 0)}
                            onClick={() => changePosition(1)}
                        >
                            <Icon icon={ICON_NAME.ARROW_LEFT} />
                        </Button>
                    </div>
                    <span>Use the arrow buttons or keys to move items left or right.</span>
                </div>}
                <div className='flex gap-[26px] pl-[30px]'>
                    <div className="link flex gap-1 items-center" onClick={handleDuplication}>
                        <Icon icon={ICON_NAME.DUPLICATE} bg="colorLink" />
                        <span>Duplicate</span>
                    </div>
                    <div
                        className="link flex gap-1 items-center"
                        data-danger={true}
                        onClick={() => onElementDelete(data?.index || 0)}
                    >
                        <Icon icon={ICON_NAME.DELETE} danger />
                        <span>Delete</span>
                    </div>
                </div>
            </>}

        </div>
    </div>;
};

export default RightControl;
